import * as v from 'valibot';
import { createEnv } from 'valibot-env/nextjs';

export const env = createEnv({
  schema: {
    /*
     * Serverside Environment variables, not available on the client.
     * Will throw if you access these variables on the client.
     */
    private: {
      ACXIOM_DOCUMENTS_SERVICE_URL: v.optional(v.pipe(v.string('ACXIOM_DOCUMENTS_SERVICE_URL must be a string'), v.url('ACXIOM_DOCUMENTS_SERVICE_URL must be a valid URL'))),
      AUTH0_ISSUER: v.fallback(v.string(), 'https://auth.nerdwallet.biz'),
      AWS_ACXIOM_SQS_ACTIVE: v.pipe(
        v.optional(v.string()),
        v.transform(value => {
          return value === undefined || value === '' ? 'IGNORE' : value;
        }),
        v.transform(value => value === 'IGNORE' || value === '1'),
        v.boolean('AWS_ACXIOM_SQS_ACTIVE must be a boolean.'),
      ),
      AWS_ACXIOM_SQS_QUEUE_URL: v.pipe(
        v.optional(v.string()),
        v.custom(input => {
          const url = input as string | undefined;
          const shouldRequireQueueUrl = process.env.AWS_ACXIOM_SQS_ACTIVE;
          if (shouldRequireQueueUrl === '1') {
            const result = v.safeParse(v.pipe(v.string(), v.nonEmpty(), v.url()), url);
            return result.success;
          }

          if (url === undefined || url === '') {
            return true;
          }
          return v.safeParse(v.pipe(v.string(), v.nonEmpty(), v.url()), url).success;
        }, 'AWS_ACXIOM_SQS_QUEUE_URL is required and must be a valid URL when AWS_ACXIOM_SQS_ACTIVE is true.'),
      ),
      AWS_ACXIOM_VERCEL_AUTH_ENABLED: v.pipe(
        v.optional(v.string()),
        v.transform(value => (value === undefined || value === '' ? 'IGNORE' : value)),
        v.transform(value => value === 'IGNORE' || value === '1'),
        v.boolean('AWS_ACXIOM_VERCEL_AUTH_ENABLED must be a boolean.'),
      ),
      AWS_VERCEL_ROLE_ARN: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('AWS_VERCEL_ROLE_ARN must be a string.'),
      ),
      GTM_ID: v.fallback(v.string(), ''),
      INTERNAL_SUPERGRAPH_URL: v.fallback(v.pipe(v.string(), v.url()), ''),
      CRON_SECRET: v.fallback(v.string(), ''),
      DATADOG_API_KEY: v.fallback(v.string(), ''),
      EDGE_CONFIG: v.fallback(v.pipe(v.string(), v.url()), ''),
      EDGE_CONFIG_ID: v.fallback(v.string(), ''),
      VERCEL_API_TOKEN: v.fallback(v.string(), ''),
      SECTION_SPECIFIC_ML_CLOUDFRONT_URL: v.fallback(v.string(), ''),
      TARGET_CONFIG: v.fallback(v.picklist(['dev', 'stage', 'prod']), 'dev'),
      LOG_LEVEL: v.fallback(
        v.picklist(['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent']),
        'info',
      ),
    },
    /*
     * Environment variables available on the client (and server).
     *
     * 💡 You'll get type errors if these are not prefixed with NEXT_PUBLIC_.
     */
    public: {
      NEXT_PUBLIC_ABSMARTLY_API_KEY: v.fallback(v.string(), ''),
      NEXT_PUBLIC_ABSMARTLY_URL: v.fallback(
        v.pipe(v.string(), v.url()),
        'https://nerdwallet.absmartly.io/v1',
      ),
      NEXT_PUBLIC_DEPLOYABLE_ENV: v.fallback(v.picklist(['dev', 'stage', 'prod']), 'dev'),
      NEXT_PUBLIC_NW_URL: v.fallback(v.pipe(v.string(), v.url()), 'https://stage.nerdwallet.biz'),
      NEXT_PUBLIC_SEGMENT_DOMAIN: v.fallback(v.string(), 'nerdwallet.biz'),
      NEXT_PUBLIC_DD_APP_ID: v.fallback(v.string(), ''),
      NEXT_PUBLIC_DD_CLIENT_TOKEN: v.fallback(v.string(), ''),
      NEXT_PUBLIC_SEGMENT_WRITE_KEY: v.fallback(v.string(), ''),
      NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: v.fallback(v.string(), ''),
    },
  },
  /*
   * Due to how Next.js bundles environment variables on Edge and Client,
   * we need to manually destructure them to make sure all are included in bundle.
   *
   * 💡 You'll get type errors if not all variables from `public` & `private` are included here.
   */
  values: {
    ACXIOM_DOCUMENTS_SERVICE_URL: process.env.ACXIOM_DOCUMENTS_SERVICE_URL,
    AUTH0_ISSUER: process.env.AUTH0_ISSUER,
    AWS_ACXIOM_SQS_ACTIVE: process.env.AWS_ACXIOM_SQS_ACTIVE,
    AWS_ACXIOM_SQS_QUEUE_URL: process.env.AWS_ACXIOM_SQS_QUEUE_URL,
    AWS_ACXIOM_VERCEL_AUTH_ENABLED: process.env.AWS_ACXIOM_VERCEL_AUTH_ENABLED,
    AWS_VERCEL_ROLE_ARN: process.env.AWS_VERCEL_ROLE_ARN,
    CRON_SECRET: process.env.CRON_SECRET,
    DATADOG_API_KEY: process.env.DATADOG_API_KEY,
    EDGE_CONFIG: process.env.EDGE_CONFIG,
    EDGE_CONFIG_ID: process.env.EDGE_CONFIG_ID,
    GTM_ID: process.env.GTM_ID,
    INTERNAL_SUPERGRAPH_URL: process.env.INTERNAL_SUPERGRAPH_URL,
    LOG_LEVEL: process.env.LOG_LEVEL,
    VERCEL_API_TOKEN: process.env.VERCEL_API_TOKEN,
    SECTION_SPECIFIC_ML_CLOUDFRONT_URL: process.env.SECTION_SPECIFIC_ML_CLOUDFRONT_URL,
    TARGET_CONFIG: process.env.TARGET_CONFIG,
    NEXT_PUBLIC_ABSMARTLY_API_KEY: process.env.NEXT_PUBLIC_ABSMARTLY_API_KEY,
    NEXT_PUBLIC_ABSMARTLY_URL: process.env.NEXT_PUBLIC_ABSMARTLY_URL,
    NEXT_PUBLIC_DEPLOYABLE_ENV: process.env.NEXT_PUBLIC_DEPLOYABLE_ENV,
    NEXT_PUBLIC_DD_APP_ID: process.env.NEXT_PUBLIC_DD_APP_ID,
    NEXT_PUBLIC_DD_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
    NEXT_PUBLIC_NW_URL: process.env.NEXT_PUBLIC_NW_URL,
    NEXT_PUBLIC_SEGMENT_DOMAIN: process.env.NEXT_PUBLIC_SEGMENT_DOMAIN,
    NEXT_PUBLIC_SEGMENT_WRITE_KEY: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  },
});
